import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ArrowLeft, Plus, Pencil, Trash, Upload, Download, MagnifyingGlass, Copy, CheckSquare } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function Products() {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [products, setProducts] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterType, setFilterType] = useState('');
  const [brands, setBrands] = useState([]);
  const [types, setTypes] = useState([]);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [deleteBrand, setDeleteBrand] = useState('');
  const [deleteType, setDeleteType] = useState('');
  const [deleteTypes, setDeleteTypes] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [formData, setFormData] = useState({
    code: '', description: '', brand: '', type: '', color: '', mrp: '', product: 'SWITCH', unit: 'NOS', catalog_number: '', size: ''
  });

  useEffect(() => { fetchProducts(); fetchBrands(); }, []);
  useEffect(() => { if (filterBrand) fetchTypes(); }, [filterBrand]);
  useEffect(() => {
    if (deleteBrand) {
      axios.get(`${BACKEND_URL}/api/types?brand=${deleteBrand}`).then(r => setDeleteTypes(r.data)).catch(() => {});
    } else {
      setDeleteTypes([]);
    }
  }, [deleteBrand]);

  const fetchProducts = async (brand, type, search) => {
    try {
      let url = `${BACKEND_URL}/api/products?`;
      if (brand || filterBrand) url += `brand=${brand || filterBrand}&`;
      if (type || filterType) url += `type=${type || filterType}&`;
      if (search || searchQuery) url += `search=${search || searchQuery}&`;
      const { data } = await axios.get(url);
      setProducts(data);
      setSelectedIds(new Set());
    } catch (e) { console.error(e); }
  };

  const fetchBrands = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/brands`);
      setBrands(data);
    } catch (e) { console.error(e); }
  };

  const fetchTypes = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/types?brand=${filterBrand}`);
      setTypes(data);
    } catch (e) { console.error(e); }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, mrp: parseFloat(formData.mrp) || 0, code: formData.code.toUpperCase() };
      if (editingProduct) {
        await axios.put(`${BACKEND_URL}/api/products/${editingProduct.id}`, payload);
        toast.success('Product updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/products`, payload);
        toast.success('Product added');
      }
      setShowAddDialog(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
      fetchBrands();
    } catch (e) { toast.error('Error saving product'); }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      code: product.code, description: product.description, brand: product.brand,
      type: product.type, color: product.color, mrp: product.mrp.toString(),
      product: product.product, unit: product.unit || 'NOS', catalog_number: product.catalog_number || '',
      size: product.size || '', switch_subtype: product.switch_subtype || ''
    });
    setShowAddDialog(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/products/${id}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (e) { toast.error('Error deleting product'); }
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/products/bulk-delete-selected`, { ids: Array.from(selectedIds) });
      toast.success(data.message);
      fetchProducts();
      fetchBrands();
    } catch (e) { toast.error('Error deleting selected products'); }
  };

  const handleBulkDelete = async () => {
    if (!deleteBrand) { toast.error('Select a brand'); return; }
    try {
      let url = `${BACKEND_URL}/api/products/bulk-delete?brand=${encodeURIComponent(deleteBrand)}`;
      if (deleteType && deleteType !== 'all') url += `&type=${encodeURIComponent(deleteType)}`;
      const { data } = await axios.delete(url);
      toast.success(data.message);
      setShowBulkDeleteDialog(false);
      setDeleteBrand('');
      setDeleteType('');
      fetchProducts();
      fetchBrands();
    } catch (e) { toast.error('Error deleting products'); }
  };

  const resetForm = () => {
    setFormData({ code: '', description: '', brand: '', type: '', color: '', mrp: '', product: 'SWITCH', unit: 'NOS', catalog_number: '', size: '', switch_subtype: '' });
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/products/bulk-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(data.message);
      fetchProducts();
      fetchBrands();
    } catch (e) { toast.error('Error uploading products'); }
    e.target.value = '';
  };

  const handleSearch = () => { fetchProducts(filterBrand, filterType, searchQuery); };
  const clearFilters = () => {
    setFilterBrand(''); setFilterType(''); setSearchQuery('');
    fetchProducts('', '', '');
  };

  const downloadTemplate = () => {
    window.location.href = `${BACKEND_URL}/api/products/template`;
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map(p => p.id)));
    }
  };

  const allSelected = products.length > 0 && selectedIds.size === products.length;

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      <header className="bg-white border-b border-border shadow-sm">
        <div className="px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft size={16} className="mr-1.5" /> Back
            </Button>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight" data-testid="products-title">Product Management</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-button">
              <Download size={16} className="mr-1.5" /> Download Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => document.getElementById('bulk-upload').click()} data-testid="bulk-upload-button">
              <Upload size={16} className="mr-1.5" /> Upload
            </Button>
            <input id="bulk-upload" type="file" accept=".xlsx,.xls" onChange={handleBulkUpload} className="hidden" />
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowBulkDeleteDialog(true)} data-testid="bulk-delete-button">
              <Trash size={16} className="mr-1.5" /> Bulk Delete
            </Button>
            <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) { setEditingProduct(null); resetForm(); } }}>
              <DialogTrigger asChild>
                <Button className="bg-[#0066FF] hover:bg-[#0052CC] text-white" data-testid="add-product-button">
                  <Plus size={16} className="mr-1.5" /> Add Product
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="code" className="text-xs">Code</Label>
                      <Input id="code" name="code" value={formData.code} onChange={handleInputChange} required placeholder="6A1W" className="font-mono" data-testid="form-code" />
                    </div>
                    <div>
                      <Label htmlFor="description" className="text-xs">Description</Label>
                      <Input id="description" name="description" value={formData.description} onChange={handleInputChange} required placeholder="6A 1WAY" data-testid="form-description" />
                    </div>
                    <div>
                      <Label htmlFor="brand" className="text-xs">Brand</Label>
                      <Input id="brand" name="brand" value={formData.brand} onChange={handleInputChange} required placeholder="GOLDMEDAL" data-testid="form-brand" />
                    </div>
                    <div>
                      <Label htmlFor="type" className="text-xs">Type</Label>
                      <Input id="type" name="type" value={formData.type} onChange={handleInputChange} required placeholder="CURVE" data-testid="form-type" />
                    </div>
                    <div>
                      <Label htmlFor="color" className="text-xs">Color</Label>
                      <Input id="color" name="color" value={formData.color} onChange={handleInputChange} required placeholder="WHITE" data-testid="form-color" />
                    </div>
                    <div>
                      <Label htmlFor="mrp" className="text-xs">MRP</Label>
                      <Input id="mrp" name="mrp" type="number" step="0.01" value={formData.mrp} onChange={handleInputChange} required data-testid="form-mrp" />
                    </div>
                    <div>
                      <Label htmlFor="catalog_number" className="text-xs">Catalog Number</Label>
                      <Input id="catalog_number" name="catalog_number" value={formData.catalog_number} onChange={handleInputChange} placeholder="681100" className="font-mono" data-testid="form-catalog-number" />
                    </div>
                    <div>
                      <Label htmlFor="size" className="text-xs">Size (Fans)</Label>
                      <Input id="size" name="size" value={formData.size} onChange={handleInputChange} placeholder="1200MM" data-testid="form-size" />
                    </div>
                    <div>
                      <Label htmlFor="switch_subtype" className="text-xs">Type</Label>
                      <Input id="switch_subtype" name="switch_subtype" value={formData.switch_subtype} onChange={handleInputChange} placeholder="G-CLICK, REGULAR" data-testid="form-switch-subtype" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Product Category</Label>
                      <select name="product" value={formData.product} onChange={handleInputChange} className="w-full mt-1 h-9 rounded-sm border border-border px-3 text-sm" data-testid="form-product">
                        <option value="SWITCH">SWITCH</option>
                        <option value="PLATE">PLATE</option>
                        <option value="WIRE">WIRE</option>
                        <option value="MCB">MCB</option>
                        <option value="ELCB">ELCB</option>
                        <option value="PIPE">PIPE</option>
                        <option value="LED PANEL">LED PANEL</option>
                        <option value="LED COB">LED COB</option>
                        <option value="LED STRIP">LED STRIP</option>
                        <option value="SMPS">SMPS</option>
                        <option value="FAN">FAN</option>
                        <option value="ACCESSORIES">ACCESSORIES</option>
                        <option value="ACCESSORY">ACCESSORY</option>
                        <option value="DEFAULT PRODUCT">DEFAULT PRODUCT</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Unit</Label>
                      <select name="unit" value={formData.unit} onChange={handleInputChange} className="w-full mt-1 h-9 rounded-sm border border-border px-3 text-sm" data-testid="form-unit">
                        <option value="NOS">NOS</option>
                        <option value="MTRS">MTRS</option>
                        <option value="KG">KG</option>
                        <option value="SET">SET</option>
                        <option value="BOX">BOX</option>
                        <option value="PKT">PKT</option>
                        <option value="ROLL">ROLL</option>
                        <option value="LOT">LOT</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                    <Button type="submit" size="sm" className="bg-[#0066FF] hover:bg-[#0052CC] text-white" data-testid="submit-product-form">
                      {editingProduct ? 'Update' : 'Add'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      {/* Bulk Delete Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-red-600">Bulk Delete Products</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This will permanently delete ALL products for the selected brand. Use this when replacing an entire price list.</p>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Brand</Label>
              <select
                value={deleteBrand}
                onChange={(e) => { setDeleteBrand(e.target.value); setDeleteType(''); }}
                className="w-full mt-1 h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm"
                data-testid="bulk-delete-brand"
              >
                <option value="">Select Brand</option>
                {brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Type (Sub-Brand) - Optional</Label>
              <select
                value={deleteType}
                onChange={(e) => setDeleteType(e.target.value)}
                disabled={!deleteBrand}
                className="w-full mt-1 h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm disabled:opacity-50"
                data-testid="bulk-delete-type"
              >
                <option value="all">All Types</option>
                {deleteTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setShowBulkDeleteDialog(false)}>Cancel</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={handleBulkDelete} disabled={!deleteBrand} data-testid="confirm-bulk-delete">
                Delete All
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="p-4 sm:p-6 max-w-[1400px] mx-auto">
        {/* Filters */}
        <div className="bg-white border border-border rounded-sm shadow-sm p-4 mb-4">
          <div className="grid grid-cols-12 gap-3 items-end">
            <div className="col-span-3">
              <Label className="text-xs">Brand</Label>
              <select
                value={filterBrand}
                onChange={(e) => { setFilterBrand(e.target.value); setFilterType(''); }}
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="filter-brand"
              >
                <option value="">All Brands</option>
                {brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className="col-span-3">
              <Label className="text-xs">Type</Label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                disabled={!filterBrand}
                className="mt-1 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="filter-type"
              >
                <option value="">All Types</option>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-span-4">
              <Label className="text-xs">Search</Label>
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by code or description"
                className="mt-1"
                data-testid="search-products"
              />
            </div>
            <div className="col-span-2 flex gap-2">
              <Button size="sm" onClick={handleSearch} className="bg-[#0066FF] hover:bg-[#0052CC] text-white" data-testid="search-button">
                <MagnifyingGlass size={16} />
              </Button>
              <Button size="sm" variant="outline" onClick={clearFilters} data-testid="clear-filters">Clear</Button>
            </div>
          </div>
        </div>

        {/* Upload Info Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 text-xs text-amber-800" data-testid="upload-info-banner">
          <strong>Excel Upload:</strong> When you upload a file, existing products of the same brand + category + sub-brand (e.g., GOLDMEDAL SWITCH NIXON) will be replaced. Other sub-brands (e.g., GOLDMEDAL SWITCH CURVE) remain untouched.
        </div>

        {/* Selected Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-3 mb-4 flex items-center justify-between" data-testid="selected-actions-bar">
            <span className="text-sm font-medium text-red-700">{selectedIds.size} product(s) selected</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())} data-testid="clear-selection">
                Clear Selection
              </Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteSelected} data-testid="delete-selected-products">
                <Trash size={14} className="mr-1.5" /> Delete Selected
              </Button>
            </div>
          </div>
        )}

        {/* Products Table */}
        <div className="bg-white border border-border rounded-sm shadow-sm overflow-hidden">
          <div className="px-4 py-2 border-b border-border text-xs text-muted-foreground">
            {products.length} product(s) found
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#111827]">
                  <TableHead className="w-[40px]">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-3.5 h-3.5 accent-white cursor-pointer"
                      data-testid="select-all-products"
                    />
                  </TableHead>
                  <TableHead className="font-medium text-white text-xs">Code</TableHead>
                  <TableHead className="font-medium text-white text-xs">Cat. No.</TableHead>
                  <TableHead className="font-medium text-white text-xs">Description</TableHead>
                  <TableHead className="font-medium text-white text-xs">Brand</TableHead>
                  <TableHead className="font-medium text-white text-xs">Type</TableHead>
                  <TableHead className="font-medium text-white text-xs">Type</TableHead>
                  <TableHead className="font-medium text-white text-xs">Color</TableHead>
                  <TableHead className="font-medium text-white text-xs">MRP</TableHead>
                  <TableHead className="font-medium text-white text-xs">Unit</TableHead>
                  <TableHead className="font-medium text-white text-xs">Product</TableHead>
                  <TableHead className="font-medium text-white text-xs w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center text-muted-foreground py-12 text-sm" data-testid="empty-products">
                      No products found. Add products or upload via Excel.
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product, idx) => (
                    <TableRow key={product.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-[#F8F9FB]'} ${selectedIds.has(product.id) ? '!bg-red-50' : ''}`}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(product.id)}
                          onChange={() => toggleSelect(product.id)}
                          className="w-3.5 h-3.5 accent-red-600 cursor-pointer"
                          data-testid={`select-product-${idx}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono font-semibold text-[#0066FF] text-xs">{product.code}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{product.catalog_number || '-'}</TableCell>
                      <TableCell className="text-xs">{product.description}</TableCell>
                      <TableCell className="text-xs font-medium">{product.brand}</TableCell>
                      <TableCell className="text-xs">{product.type}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{product.switch_subtype || '-'}</TableCell>
                      <TableCell className="text-xs">{product.color}</TableCell>
                      <TableCell className="text-xs font-medium">{product.mrp}</TableCell>
                      <TableCell className="text-xs">{product.unit || 'NOS'}</TableCell>
                      <TableCell className="text-xs">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          product.product === 'SWITCH' ? 'bg-blue-100 text-blue-700' :
                          product.product === 'PLATE' ? 'bg-amber-100 text-amber-700' :
                          product.product === 'WIRE' ? 'bg-green-100 text-green-700' :
                          product.product === 'PROJECT WIRE' ? 'bg-emerald-100 text-emerald-700' :
                          product.product === 'MCB' ? 'bg-purple-100 text-purple-700' :
                          product.product === 'ELCB' ? 'bg-red-100 text-red-700' :
                          product.product === 'PIPE' ? 'bg-teal-100 text-teal-700' :
                          product.product === 'ACCESSORIES' ? 'bg-violet-100 text-violet-700' :
                          product.product === 'FAN' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{product.product}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <button onClick={() => handleEdit(product)} className="text-muted-foreground hover:text-foreground p-1" data-testid={`edit-product-${idx}`}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => handleDelete(product.id)} className="text-red-400 hover:text-red-600 p-1" data-testid={`delete-product-${idx}`}>
                            <Trash size={14} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <div className="h-16"></div>
        </div>
        <div className="h-14"></div>
      </div>
    </div>
  );
}
